// Admin · Hot Leads Dashboard — calculator submissions ranked by approval tier.
import { useEffect, useState, useCallback, useMemo } from "react";
import {
  Flame, Phone, Mail, Copy, Check, X, RefreshCw, Inbox,
  TrendingUp, Users, Target, Award,
} from "lucide-react";
import AdminLayout from "../components/AdminLayout";
import { useAuth } from "../auth/AuthContext";

const API = process.env.REACT_APP_BACKEND_URL;

const PERIODS = [
  { value: "24h", label: "24 hours" },
  { value: "7d", label: "7 days" },
  { value: "30d", label: "30 days" },
];

const STATUS_OPTIONS = ["New", "Contacted", "Booked", "Converted", "Cold"];

const TIER_META = {
  "Strong Profile": { bg: "#14281E", text: "#F2EBDD", label: "Strong Profile" },
  "Good Standing": { bg: "#C8A46A", text: "#14281E", label: "Good Standing" },
  "Building Phase": { bg: "#5C1A24", text: "#F2EBDD", label: "Building Phase" },
  "Credit Profile Development Recommended First": { bg: "#9BA39C", text: "#2A1C15", label: "Credit Development" },
  "Business Foundation + Credit Strategy": { bg: "#9BA39C", text: "#2A1C15", label: "Business Foundation" },
};

function tierBadge(tier) {
  const meta = TIER_META[tier] || { bg: "#9BA39C", text: "#2A1C15", label: tier || "—" };
  return (
    <span
      className="inline-flex items-center px-3 py-1 rounded-sm font-mono text-[10px] uppercase tracking-[0.15em] whitespace-nowrap"
      style={{ backgroundColor: meta.bg, color: meta.text }}
    >
      {meta.label}
    </span>
  );
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  } catch {
    return iso.slice(0, 10);
  }
}

function fmtTime(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" });
  } catch {
    return "";
  }
}

// ---------- atoms ----------
const StatCard = ({ icon: Icon, label, value, testId, suffix }) => (
  <div className="bg-jc-card border border-jc-gold/30 rounded-lg p-5 md:p-6" data-testid={testId}>
    <div className="flex items-start justify-between gap-3">
      <div>
        <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-jc-ink-muted">
          {label}
        </div>
        <div className="mt-3 flex items-baseline gap-2">
          <div className="font-display text-4xl md:text-5xl text-jc-gold leading-none">
            {value}
          </div>
          {suffix ? (
            <div className="font-display text-2xl text-jc-gold">{suffix}</div>
          ) : null}
        </div>
      </div>
      <div className="h-9 w-9 rounded-md bg-jc-forest/8 border border-jc-gold/30 flex items-center justify-center text-jc-forest flex-shrink-0">
        <Icon size={16} strokeWidth={2} />
      </div>
    </div>
  </div>
);

const PeriodToggle = ({ period, onChange }) => (
  <div
    className="inline-flex rounded-md border border-jc-gold/30 bg-jc-card p-1"
    role="group"
    aria-label="Time window"
    data-testid="hotleads-period-toggle"
  >
    {PERIODS.map((p) => (
      <button
        key={p.value}
        onClick={() => onChange(p.value)}
        data-testid={`hotleads-period-${p.value}`}
        className={`px-4 py-2 font-mono text-[11px] uppercase tracking-[0.18em] rounded-sm transition-colors ${
          period === p.value
            ? "bg-jc-forest text-jc-ivory"
            : "text-jc-ink-muted hover:text-jc-forest"
        }`}
      >
        {p.label}
      </button>
    ))}
  </div>
);

const StatusDropdown = ({ value, onChange, recordId, disabled }) => (
  <select
    value={value || "New"}
    onChange={(e) => onChange(recordId, e.target.value)}
    disabled={disabled}
    data-testid={`hotleads-status-${recordId}`}
    className={`text-xs font-mono uppercase tracking-[0.12em] px-2.5 py-1.5 rounded-sm border bg-jc-card text-jc-ink border-jc-gold/30 focus:outline-none focus:border-jc-gold cursor-pointer ${
      disabled ? "opacity-60 cursor-wait" : ""
    }`}
  >
    {STATUS_OPTIONS.map((s) => (
      <option key={s} value={s}>{s}</option>
    ))}
  </select>
);

// ---------- Contact Drawer ----------
function ContactDrawer({ lead, onClose, onMarkContacted }) {
  const [copied, setCopied] = useState(false);
  if (!lead) return null;
  const subject = encodeURIComponent("Your JC3 Financial Strategy — Next Steps");
  const mailto = lead.email ? `mailto:${lead.email}?subject=${subject}` : null;

  const copyPhone = () => {
    if (!lead.phone) return;
    navigator.clipboard.writeText(lead.phone).then(
      () => {
        setCopied(true);
        setTimeout(() => setCopied(false), 1800);
      },
      () => {}
    );
  };

  return (
    <div className="fixed inset-0 z-50" data-testid="hotleads-contact-drawer">
      <div className="absolute inset-0 bg-jc-forest-deep/70 backdrop-blur-sm" onClick={onClose} />
      <div className="absolute inset-y-0 right-0 w-full max-w-md bg-jc-ivory border-l border-jc-gold/40 flex flex-col shadow-2xl">
        <div className="px-7 py-6 border-b border-jc-gold/20 flex items-start justify-between bg-jc-forest text-jc-ivory">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-jc-gold">Contact Lead</p>
            <h3 className="mt-2 font-display text-2xl text-jc-ivory leading-tight">
              {lead.name || lead.email || "Anonymous Lead"}
            </h3>
            {lead.tier ? <div className="mt-3">{tierBadge(lead.tier)}</div> : null}
          </div>
          <button
            onClick={onClose}
            data-testid="hotleads-drawer-close"
            className="h-9 w-9 inline-flex items-center justify-center border border-jc-gold/40 text-jc-gold hover:bg-jc-gold hover:text-jc-forest rounded-sm"
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-7 py-6 space-y-5">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-jc-ink-muted mb-1">Email</div>
            <div className="font-body text-base text-jc-ink break-all">{lead.email || "—"}</div>
          </div>
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-jc-ink-muted mb-1">Phone</div>
            <div className="font-body text-base text-jc-ink">{lead.phone || "—"}</div>
          </div>
          <div className="pt-3 border-t border-jc-gold/20">
            <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-jc-ink-muted mb-2">Profile</div>
            <ul className="space-y-1.5 font-body text-sm text-jc-ink">
              <li><span className="text-jc-ink-muted">Approval Range:</span> {lead.approval_range || "—"}</li>
              <li><span className="text-jc-ink-muted">Primary Goal:</span> {lead.primary_goal || "—"}</li>
              <li><span className="text-jc-ink-muted">Business Stage:</span> {lead.business_stage || "—"}</li>
              <li><span className="text-jc-ink-muted">Blocker:</span> {lead.blocker || "—"}</li>
              <li><span className="text-jc-ink-muted">Funding Goal:</span> {lead.funding_goal || "—"}</li>
            </ul>
          </div>
        </div>

        <div className="px-7 py-6 border-t border-jc-gold/20 space-y-3 bg-jc-card">
          <a
            href={mailto || "#"}
            onClick={(e) => { if (!mailto) e.preventDefault(); }}
            data-testid="hotleads-send-email"
            className={`jc-btn w-full ${mailto ? "jc-btn-primary" : "jc-btn-outline-forest opacity-50 cursor-not-allowed"}`}
          >
            <Mail size={14} strokeWidth={2.5} /> Send Email
          </a>
          <button
            type="button"
            onClick={copyPhone}
            disabled={!lead.phone}
            data-testid="hotleads-copy-phone"
            className={`jc-btn w-full ${lead.phone ? "jc-btn-secondary" : "jc-btn-secondary opacity-50 cursor-not-allowed"}`}
          >
            {copied ? <Check size={14} strokeWidth={2.5} /> : <Copy size={14} strokeWidth={2.5} />}
            {copied ? "Copied!" : "Copy Phone"}
          </button>
          <button
            type="button"
            onClick={() => onMarkContacted(lead.id)}
            data-testid="hotleads-mark-contacted"
            className="jc-btn jc-btn-outline-forest w-full"
          >
            <Check size={14} strokeWidth={2.5} /> Mark as Contacted
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------- Page ----------
export default function AdminLeads() {
  const { token } = useAuth();
  const [period, setPeriod] = useState("7d");
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [savingStatus, setSavingStatus] = useState(null); // record_id
  const [drawerLead, setDrawerLead] = useState(null);

  const authHeaders = useMemo(
    () => ({ "Content-Type": "application/json", Authorization: `Bearer ${token}` }),
    [token]
  );

  const load = useCallback(async () => {
    setError(null);
    try {
      const [leadsRes, statsRes] = await Promise.all([
        fetch(`${API}/api/admin/leads?period=${period}`, { headers: authHeaders }),
        fetch(`${API}/api/admin/leads/stats?period=${period}`, { headers: authHeaders }),
      ]);
      if (!leadsRes.ok || !statsRes.ok) throw new Error("load_failed");
      const leadsJson = await leadsRes.json();
      const statsJson = await statsRes.json();
      setLeads(leadsJson.records || []);
      setStats(statsJson);
    } catch (e) {
      setError(e?.message || "load_failed");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [period, authHeaders]);

  useEffect(() => {
    setLoading(true);
    load();
  }, [load]);

  const handleRefresh = () => {
    setRefreshing(true);
    load();
  };

  const handleStatusChange = async (recordId, newStatus) => {
    setSavingStatus(recordId);
    // optimistic update
    setLeads((prev) => prev.map((l) => (l.id === recordId ? { ...l, status: newStatus } : l)));
    try {
      const res = await fetch(`${API}/api/admin/leads/${recordId}/status`, {
        method: "PATCH",
        headers: authHeaders,
        body: JSON.stringify({ status: newStatus }),
      });
      if (!res.ok) throw new Error("save_failed");
      // If Converted — refresh stats since conversion rate may change
      if (newStatus === "Converted") {
        await load();
      }
    } catch {
      // rollback by re-loading
      await load();
    } finally {
      setSavingStatus(null);
    }
  };

  const handleMarkContacted = async (recordId) => {
    await handleStatusChange(recordId, "Contacted");
    setDrawerLead(null);
  };

  const isEmpty = !loading && leads.length === 0;

  return (
    <AdminLayout title="Hot Leads" kicker="Admin · Lead Prospecting Dashboard">
      <div className="-mt-2 mb-7 max-w-2xl">
        <p className="font-body text-jc-muted text-base leading-relaxed">
          Calculator submissions ranked by approval tier. Work the list daily.
        </p>
      </div>

      {/* Stat cards */}
      <div data-testid="hotleads-stats-grid" className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5 mb-9">
        <StatCard
          testId="hotleads-stat-total"
          icon={Users}
          label="Total Leads"
          value={stats ? stats.total_leads : "—"}
        />
        <StatCard
          testId="hotleads-stat-today"
          icon={TrendingUp}
          label="New Today"
          value={stats ? stats.new_today : "—"}
        />
        <StatCard
          testId="hotleads-stat-strong"
          icon={Award}
          label="Strong Profiles"
          value={stats ? stats.strong_profiles : "—"}
        />
        <StatCard
          testId="hotleads-stat-conversion"
          icon={Target}
          label="Conversion Rate"
          value={stats ? stats.conversion_rate : "—"}
          suffix={stats ? "%" : null}
        />
      </div>

      {/* Toolbar */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <PeriodToggle period={period} onChange={setPeriod} />
        <button
          type="button"
          onClick={handleRefresh}
          data-testid="hotleads-refresh"
          className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.18em] text-jc-ink-muted hover:text-jc-forest transition-colors"
        >
          <RefreshCw size={13} className={refreshing ? "animate-spin" : ""} />
          {refreshing ? "Refreshing…" : "Refresh"}
        </button>
      </div>

      {/* Empty state */}
      {isEmpty ? (
        <div
          data-testid="hotleads-empty"
          className="bg-jc-forest text-jc-ivory rounded-lg p-10 md:p-14 text-center"
        >
          <div className="inline-flex h-14 w-14 rounded-full bg-jc-gold/15 border border-jc-gold/40 items-center justify-center text-jc-gold mb-5">
            <Inbox size={22} />
          </div>
          <p className="font-display text-2xl md:text-3xl text-jc-ivory leading-tight">
            No leads in this period.
          </p>
          <p className="mt-3 font-body text-jc-ivory-muted text-base">
            Share the homepage calculator to start filling your pipeline.
          </p>
        </div>
      ) : null}

      {/* Error state */}
      {error && !loading ? (
        <div className="bg-jc-burgundy/10 border border-jc-burgundy/40 rounded-md p-5 mb-5">
          <p className="font-body text-sm text-jc-ink">Failed to load leads. Click Refresh to retry.</p>
        </div>
      ) : null}

      {/* Loading state */}
      {loading ? (
        <div className="bg-jc-card border border-jc-gold/20 rounded-lg p-10 text-center font-mono text-[11px] uppercase tracking-[0.22em] text-jc-ink-muted">
          Loading leads…
        </div>
      ) : null}

      {/* Table */}
      {!loading && !isEmpty ? (
        <div className="bg-jc-card border border-jc-gold/20 rounded-lg overflow-hidden" data-testid="hotleads-table-card">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="hotleads-table">
              <thead>
                <tr className="text-left font-mono text-[10px] uppercase tracking-[0.18em] text-jc-ink-muted bg-jc-ivory-soft border-b border-jc-gold/20">
                  <th className="px-4 py-3.5">Timestamp</th>
                  <th className="px-4 py-3.5">Tier</th>
                  <th className="px-4 py-3.5">Name</th>
                  <th className="px-4 py-3.5">Email</th>
                  <th className="px-4 py-3.5">Phone</th>
                  <th className="px-4 py-3.5">Approval Range</th>
                  <th className="px-4 py-3.5 hidden xl:table-cell">Primary Goal</th>
                  <th className="px-4 py-3.5 hidden xl:table-cell">Business Stage</th>
                  <th className="px-4 py-3.5 hidden xl:table-cell">Blocker</th>
                  <th className="px-4 py-3.5 hidden xl:table-cell">Funding Goal</th>
                  <th className="px-4 py-3.5">Status</th>
                  <th className="px-4 py-3.5">Action</th>
                </tr>
              </thead>
              <tbody>
                {leads.map((l) => (
                  <tr
                    key={l.id}
                    data-testid={`hotleads-row-${l.id}`}
                    className="border-b border-jc-gold/10 hover:bg-jc-ivory-soft/40 transition-colors text-jc-ink"
                  >
                    <td className="px-4 py-3 whitespace-nowrap font-body text-[13px]">
                      <div>{fmtDate(l.createdTime || l.Timestamp)}</div>
                      <div className="text-jc-ink-muted text-[11px]">{fmtTime(l.createdTime || l.Timestamp)}</div>
                    </td>
                    <td className="px-4 py-3">{tierBadge(l.tier)}</td>
                    <td className="px-4 py-3 font-body text-[13.5px] whitespace-nowrap">{l.name || <span className="text-jc-ink-muted">—</span>}</td>
                    <td className="px-4 py-3 font-body text-[13px] text-jc-ink-muted break-all max-w-[180px]">{l.email || "—"}</td>
                    <td className="px-4 py-3 font-body text-[13px] text-jc-ink-muted whitespace-nowrap">{l.phone || "—"}</td>
                    <td className="px-4 py-3 font-body text-[12.5px] text-jc-ink min-w-[180px]">{l.approval_range || "—"}</td>
                    <td className="px-4 py-3 hidden xl:table-cell font-body text-[12.5px]">{l.primary_goal || "—"}</td>
                    <td className="px-4 py-3 hidden xl:table-cell font-body text-[12.5px]">{l.business_stage || "—"}</td>
                    <td className="px-4 py-3 hidden xl:table-cell font-body text-[12.5px]">{l.blocker || "—"}</td>
                    <td className="px-4 py-3 hidden xl:table-cell font-body text-[12.5px]">{l.funding_goal || "—"}</td>
                    <td className="px-4 py-3">
                      <StatusDropdown
                        value={l.status}
                        recordId={l.id}
                        onChange={handleStatusChange}
                        disabled={savingStatus === l.id}
                      />
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <button
                        type="button"
                        onClick={() => setDrawerLead(l)}
                        data-testid={`hotleads-contact-${l.id}`}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm border border-jc-forest bg-jc-forest text-jc-ivory text-[11px] font-mono uppercase tracking-[0.15em] hover:bg-jc-forest-deep transition-colors"
                      >
                        <Flame size={12} strokeWidth={2.5} />
                        Contact
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}

      <ContactDrawer
        lead={drawerLead}
        onClose={() => setDrawerLead(null)}
        onMarkContacted={handleMarkContacted}
      />
    </AdminLayout>
  );
}
